import React, { Suspense } from 'react';
import PropTypes from 'prop-types';
import { Provider, useSelector } from 'react-redux';
import { Router } from 'react-router-dom';
import { persistStore } from 'redux-persist';
import { QueryClientProvider } from 'react-query';
import { PersistGate } from 'redux-persist/integration/react';
import {
  GrowthBookProvider as InitialGrowthBookProvider,
  useFeature,
} from '@growthbook/growthbook-react';
import Loader from './components/Loader/Loader';
import RouterContextProvider from './components/Router/RouterContextProvider';
import SEOQueryProviders from './components/SEOWrapper/SEOQueryProviders';
import ErrorBoundary from './components/Error/ErrorBoundary/ErrorBoundary';
import Maintenance from './components/Maintenance/Maintenance';
import Routes from './Routes';
import SeoTextLoadingProvider from './AppSCCOM/components/SEOWrapper/SeoTextLoadingProvider/SeoTextLoadingProvider';
import { queryClient } from './services/queryClient';
import { useGrowthBook } from './services/growthbook';
import { isLocalizationInitializedSelector } from './redux/reducers/app/initialization';
import { FEATURES } from './constants/featureToggling';

import './assets/styles/index.css';
import '@modulor/react/dist/index.css';

export const GrowthBookProvider = ({ children }) => {
  const { data, isLoading } = useGrowthBook();
  const isLocalizationReady = useSelector(isLocalizationInitializedSelector);

  if (isLoading || !isLocalizationReady) {
    return null;
  }

  return (
    <InitialGrowthBookProvider growthbook={data}>
      {children}
    </InitialGrowthBookProvider>
  );
};

const AppRoutes = () => {
  const isMaintenanceMode = useFeature(FEATURES.SHOW_MAINTENANCE_PAGE).on;

  const RoutesComponent = () => (
    <Suspense fallback={<Loader />}>
      <Routes />
    </Suspense>
  );

  return isMaintenanceMode ? <Maintenance /> : <RoutesComponent />;
};

const App = ({ store, history }) => (
  <Provider store={store}>
    <GrowthBookProvider>
      <PersistGate persistor={persistStore(store)}>
        <SEOQueryProviders>
          <QueryClientProvider client={queryClient}>
            <SeoTextLoadingProvider>
              <Router history={history}>
                <ErrorBoundary showDialog>
                  <RouterContextProvider>
                    <AppRoutes />
                  </RouterContextProvider>
                </ErrorBoundary>
              </Router>
            </SeoTextLoadingProvider>
          </QueryClientProvider>
        </SEOQueryProviders>
      </PersistGate>
    </GrowthBookProvider>
  </Provider>
);

App.propTypes = {
  /* Redux store */
  store: PropTypes.shape({
    dispatch: PropTypes.func.isRequired,
    getState: PropTypes.func.isRequired,
    subscribe: PropTypes.func.isRequired,
  }).isRequired,
  /* Basename for react-router-dom */
  history: PropTypes.object.isRequired,
};

export default App;
